// src/components/Jobs.tsx
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import { axiosInstance } from "../utils/axiosConfig";
import ApplyModal, { ApplyJob } from "./ApplyModal";
import AdBanner from "./AdBanner";
import { cleanText, formatPostedDate, BOARD_LABELS } from "../utils/textClean";

interface Job {
  id: string;
  title: string;
  company: string;
  location: string;
  description: string;
  salary: string;
  url: string;
  postedAt: string;
  board?: string; // source job board (adzuna, remoteok, ...)
  matchScore: number;
  matchBasis: string;
}


const Jobs: React.FC = () => {
  const navigate = useNavigate();
  const [jobs, setJobs] = useState<Job[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<"all" | "high-match" | "recent">("all");
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [keywords, setKeywords] = useState<string[]>([]);
  const [savedJobs, setSavedJobs] = useState<Set<string>>(new Set());
  const [appliedJobs, setAppliedJobs] = useState<Set<string>>(new Set());
  const [applyJob, setApplyJob] = useState<ApplyJob | null>(null);

  useEffect(() => {
    const fetchJobs = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await axiosInstance.get("/auth/jobs/recommendations");
        const { jobs: jobsData, searchQuery: query, keywords: kw } = response.data;
        
        setJobs(jobsData);
        setSearchQuery(query);
        setKeywords(kw || []);

        // Fetch saved jobs
        const savedResponse = await axiosInstance.get("/auth/jobs/saved");
        const savedJobIds = new Set<string>(savedResponse.data.jobs.map((j: any) => j.adzunaJobId));
        setSavedJobs(savedJobIds);

        // Fetch applied jobs
        const appliedResponse = await axiosInstance.get("/auth/jobs/applications");
        const appliedJobIds = new Set<string>(appliedResponse.data.applications.map((a: any) => a.adzunaJobId));
        setAppliedJobs(appliedJobIds);

        if (jobsData.length === 0) {
          toast.success("No jobs found for your profile. Keep building your resume!");
        }
      } catch (err: any) {
        const errorMsg = err.response?.data?.message || err.response?.data?.error || "Failed to load job recommendations";
        setError(errorMsg);
        toast.error(errorMsg);
      } finally {
        setLoading(false);
      }
    };

    fetchJobs();
  }, []);

  const handleSaveJob = async (job: Job) => {
    try {
      await axiosInstance.post("/auth/jobs/save", {
        adzunaJobId: job.id,
        jobTitle: job.title,
        company: job.company,
        location: job.location,
        salary: job.salary,
        url: job.url,
      });
      const newSavedJobs = new Set(savedJobs);
      newSavedJobs.add(job.id);
      setSavedJobs(newSavedJobs);
      toast.success("Job saved!");
    } catch (err: any) {
      toast.error(err.response?.data?.error || "Failed to save job");
    }
  };

  // Open the Track modal so the user can confirm the resume used before the
  // application is recorded with full info.
  const handleApplyJob = (job: Job) => {
    setApplyJob({
      id: job.id,
      title: job.title,
      company: job.company,
      location: job.location,
      url: job.url,
      description: job.description,
    });
  };

  const markApplied = (jobId: string) => {
    setAppliedJobs((prev) => new Set(prev).add(jobId));
  };

  const filteredJobs = jobs.filter(job => {
    if (activeTab === "high-match") return job.matchScore >= 70;
    if (activeTab === "recent") return true; // Already sorted by recency from backend
    return true; // all
  });

  if (loading) {
    return (
      <div style={{ padding: "40px 20px", textAlign: "center", maxWidth: "1200px", margin: "0 auto" }}>
        <p style={{ fontSize: "16px", color: "#666" }}>🔍 Fetching personalized job recommendations...</p>
      </div>
    );
  }

  if (error && jobs.length === 0) {
    return (
      <div style={{ padding: "40px 20px", maxWidth: "1200px", margin: "0 auto" }}>
        <button
          onClick={() => navigate("/dashboard")}
          style={{
            marginBottom: "20px",
            padding: "10px 20px",
            background: "#ccc",
            border: "none",
            borderRadius: "6px",
            cursor: "pointer",
          }}
        >
          Back to Dashboard
        </button>
        <h2>Job Recommendations</h2>
        <div style={{
          padding: "20px",
          background: "#fee",
          border: "1px solid #f99",
          borderRadius: "8px",
          marginBottom: "20px"
        }}>
          <p style={{ color: "#c33", marginBottom: "10px" }}>⚠️ {error}</p>
          <p style={{ color: "#666", fontSize: "14px", margin: "0" }}>
            Please ensure ADZUNA_APP_ID and ADZUNA_API_KEY are configured in your .env file.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div style={{ padding: "20px", maxWidth: "1200px", margin: "0 auto" }}>
      <button
        onClick={() => navigate("/dashboard")}
        style={{
          marginBottom: "20px",
          padding: "10px 20px",
          background: "#ccc",
          border: "none",
          borderRadius: "6px",
          cursor: "pointer",
        }}
      >
        Back to Dashboard
      </button>

      <h2>Job Recommendations</h2>
      <p style={{ color: "#666", marginBottom: "10px" }}>
        Personalized matches based on your resume and skills
      </p>

      {searchQuery && (
        <div style={{ marginBottom: "20px", padding: "15px", background: "#f0f7ff", borderRadius: "6px" }}>
          <p style={{ margin: "0 0 10px 0", fontSize: "14px", fontWeight: "bold" }}>
            Search Query: <span style={{ color: "#1976d2" }}>{searchQuery}</span>
          </p>
          {keywords.length > 0 && (
            <p style={{ margin: "0", fontSize: "13px", color: "#555" }}>
              <strong>Matched Skills:</strong> {keywords.join(", ")}
            </p>
          )}
        </div>
      )}

      {/* Tabs */}
      <div style={{ marginBottom: "20px", display: "flex", gap: "10px", flexWrap: "wrap" }}>
        <button
          onClick={() => setActiveTab("all")}
          style={{
            padding: "10px 20px",
            background: activeTab === "all" ? "#1976d2" : "#eee",
            color: activeTab === "all" ? "white" : "black",
            border: "none",
            borderRadius: "6px",
            cursor: "pointer",
            fontWeight: activeTab === "all" ? "bold" : "normal",
          }}
        >
          All Jobs ({jobs.length})
        </button>
        <button
          onClick={() => setActiveTab("high-match")}
          style={{
            padding: "10px 20px",
            background: activeTab === "high-match" ? "#1976d2" : "#eee",
            color: activeTab === "high-match" ? "white" : "black",
            border: "none",
            borderRadius: "6px",
            cursor: "pointer",
            fontWeight: activeTab === "high-match" ? "bold" : "normal",
          }}
        >
          High Match (70%+) ({jobs.filter(j => j.matchScore >= 70).length})
        </button>
        <button
          onClick={() => setActiveTab("recent")}
          style={{
            padding: "10px 20px",
            background: activeTab === "recent" ? "#1976d2" : "#eee",
            color: activeTab === "recent" ? "white" : "black",
            border: "none",
            borderRadius: "6px",
            cursor: "pointer",
            fontWeight: activeTab === "recent" ? "bold" : "normal",
          }}
        >
          Recently Posted
        </button>
      </div>

      {/* Job List */}
      {filteredJobs.length === 0 ? (
        <p style={{ textAlign: "center", color: "#999", padding: "40px 20px" }}>
          No jobs match this filter. Try browsing all jobs.
        </p>
      ) : (
        <div style={{ display: "grid", gap: "20px" }}>
          {filteredJobs.map((job, jobIdx) => (
            <React.Fragment key={job.id}>
            <div
              style={{
                padding: "20px",
                background: job.matchScore >= 80 ? "#f0f8f0" : "#f9f9f9",
                borderRadius: "8px",
                border: job.matchScore >= 80 ? "2px solid #4caf50" : "1px solid #ddd",
                borderLeft: `5px solid ${job.matchScore >= 80 ? "#4caf50" : job.matchScore >= 70 ? "#ff9800" : "#2196f3"}`,
              }}
            >
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "start" }}>
                <div style={{ flex: 1 }}>
                  <h3 style={{ margin: "0 0 5px 0" }}>{job.title}</h3>
                  <p style={{ color: "#555", margin: "0 0 10px 0", fontWeight: "500" }}>
                    {job.company} • {cleanText(job.location) || "Location not specified"}
                    {job.board && BOARD_LABELS[job.board] && (
                      <span
                        style={{
                          marginLeft: "10px",
                          fontSize: "12px",
                          fontWeight: 600,
                          color: "#1976d2",
                          background: "#e3f2fd",
                          borderRadius: "10px",
                          padding: "2px 10px",
                          verticalAlign: "middle",
                        }}
                        title={`Found on ${BOARD_LABELS[job.board]}`}
                      >
                        via {BOARD_LABELS[job.board]}
                      </span>
                    )}
                  </p>
                  <p style={{ margin: "10px 0", color: "#666", fontSize: "14px" }}>
                    {cleanText(job.description).substring(0, 250)}
                    {cleanText(job.description).length > 250 ? "..." : ""}
                  </p>
                  <div style={{ display: "flex", gap: "20px", marginTop: "15px", flexWrap: "wrap" }}>
                    <span style={{ fontSize: "14px" }}>
                      <strong>💰 Salary:</strong> {job.salary}
                    </span>
                    <span style={{ fontSize: "14px", color: "#666" }}>
                      <strong>📅 Posted:</strong> {formatPostedDate(job.postedAt)}
                    </span>
                  </div>
                </div>
                <div style={{ marginLeft: "20px", textAlign: "center", minWidth: "120px" }}>
                  <div
                    style={{
                      width: "100px",
                      height: "100px",
                      borderRadius: "50%",
                      background: job.matchScore >= 80 ? "#4caf50" : job.matchScore >= 70 ? "#ff9800" : "#2196f3",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: "white",
                      fontSize: "28px",
                      fontWeight: "bold",
                      margin: "0 auto 10px",
                    }}
                  >
                    {job.matchScore}%
                  </div>
                  <p style={{ fontSize: "12px", color: "#666", margin: "0" }}>Match Score</p>
                </div>
              </div>
              <p style={{ fontSize: "13px", color: "#888", margin: "15px 0 0 0" }}>
                {job.matchBasis}
              </p>
              <div style={{ display: "flex", gap: "10px", marginTop: "15px", flexWrap: "wrap" }}>
                <button
                  onClick={() => handleSaveJob(job)}
                  disabled={savedJobs.has(job.id)}
                  style={{
                    padding: "10px 20px",
                    background: savedJobs.has(job.id) ? "#95a5a6" : "#27ae60",
                    color: "white",
                    border: "none",
                    borderRadius: "6px",
                    cursor: savedJobs.has(job.id) ? "default" : "pointer",
                    fontSize: "14px",
                    fontWeight: "bold",
                  }}
                >
                  {savedJobs.has(job.id) ? "✓ Saved" : "📌 Save"}
                </button>
                <button
                  onClick={() => handleApplyJob(job)}
                  disabled={appliedJobs.has(job.id)}
                  style={{
                    padding: "10px 20px",
                    background: appliedJobs.has(job.id) ? "#95a5a6" : "#e74c3c",
                    color: "white",
                    border: "none",
                    borderRadius: "6px",
                    cursor: appliedJobs.has(job.id) ? "default" : "pointer",
                    fontSize: "14px",
                    fontWeight: "bold",
                  }}
                >
                  {appliedJobs.has(job.id) ? "✓ Applied" : "✉ Apply"}
                </button>
                <button
                  onClick={() => window.open(job.url, "_blank")}
                  style={{
                    padding: "10px 20px",
                    background: "#1976d2",
                    color: "white",
                    border: "none",
                    borderRadius: "6px",
                    cursor: "pointer",
                    fontSize: "14px",
                    fontWeight: "bold",
                  }}
                >
                  View Job →
                </button>
              </div>
            </div>
            {/* Phase 15.3 — job-detail ad slot immediately below the Apply
                button of the top job card (design 10.3 AC-2). AdBanner
                self-hides for paid users and when no ads are available. */}
            {jobIdx === 0 && <AdBanner slot="job_detail" autoRotateInterval={8000} />}
            </React.Fragment>
          ))}
        </div>
      )}

      <div style={{ marginTop: "40px", padding: "20px", background: "#f0f7ff", borderRadius: "8px", textAlign: "center" }}>
        <p style={{ margin: "0 0 12px 0", color: "#555", fontSize: "15px" }}>
          Looking for something more specific?
        </p>
        <button
          onClick={() => navigate("/job-search")}
          style={{
            padding: "12px 24px",
            background: "#1976d2",
            color: "white",
            border: "none",
            borderRadius: "6px",
            cursor: "pointer",
            fontSize: "15px",
            fontWeight: "bold",
          }}
        >
          🔍 Advanced Search
        </button>
      </div>

      {applyJob && (
        <ApplyModal
          job={applyJob}
          onClose={() => setApplyJob(null)}
          onApplied={markApplied}
        />
      )}
    </div>
  );
};

export default Jobs;